import { RegisterOptions, FieldValues, Path } from "react-hook-form";
import { APIEndpoint } from "models/general";
import dayjs from "dayjs";

/**
 * Enum for all supported field types
 */
export enum FieldType {
  TEXT = "text",
  EMAIL = "email",
  PASSWORD = "password",
  NUMBER = "number",
  TEXTAREA = "textarea",
  CHECKBOX = "checkbox",
  RADIO = "radio",
  SELECT = "select",
  DATE = "date",
  HIDDEN = "hidden",
}

/**
 * Base configuration shared by all field types
 */
export interface BaseFieldConfig<T = any> {
  /** Unique identifier for the field - used as the field name in the form */
  name: string;

  /** Field type */
  type: FieldType;

  /** Label text displayed for the field */
  label?: string;

  /** Placeholder text */
  placeholder?: string;

  /** Default value for the field */
  defaultValue?: T;

  /** Whether the field is disabled */
  disabled?: boolean;

  /** Whether the field is read-only */
  readOnly?: boolean;

  /** CSS class for the parent container */
  className?: string;

  /** Tooltip or help text */
  tooltip?: string;

  /** React Hook Form validation rules */
  validation?: RegisterOptions;

  /** Custom onChange handler - receives the new value */
  onChange?: (value: T) => void;

  /** Custom onBlur handler */
  onBlur?: () => void;

  /** Whether to hide the field */
  hidden?: boolean;

  /** Custom data attributes */
  data?: Record<string, any>;
}

/**
 * Configuration for text-based input fields (text, email, password, number)
 */
export interface TextFieldConfig extends BaseFieldConfig<string> {
  type:
    | FieldType.TEXT
    | FieldType.EMAIL
    | FieldType.PASSWORD
    | FieldType.NUMBER;

  /** Maximum character length */
  maxLength?: number;

  /** Minimum character length */
  minLength?: number;

  /** Maximum value (for number type) */
  max?: number;

  /** Minimum value (for number type) */
  min?: number;

  /** Pattern for validation */
  pattern?: RegExp;

  /** Auto-complete attribute */
  autoComplete?: string;

  /** Custom onEnter handler */
  onEnter?: (value: string) => void;
}

/**
 * Configuration for textarea fields
 */
export interface TextAreaFieldConfig extends BaseFieldConfig<string> {
  type: FieldType.TEXTAREA;

  /** Number of visible rows */
  rows?: number;

  /** Maximum character length */
  maxLength?: number;

  /** Minimum character length */
  minLength?: number;

  /** Whether to auto-resize based on content */
  autoSize?: boolean | { minRows?: number; maxRows?: number };
}

/**
 * Option item for select, radio, and checkbox fields
 */
export interface FieldOption<T = any> {
  /** Display label */
  label: string;

  /** Value for this option */
  value: T;

  /** Whether this option is disabled */
  disabled?: boolean;

  /** Additional data for this option */
  data?: Record<string, any>;
}

/**
 * Parameter configuration for async data fetching
 */
export interface FetchParameter {
  /** Parameter key */
  key: string;

  /** Static value */
  value?: any;

  /** Reference to another field's value */
  fieldReference?: string;
}

/**
 * Configuration for fetching dynamic options
 */
export interface FetchConfig<T = any> {
  /** API endpoint - use this OR serviceFn */
  url?: APIEndpoint;

  /** HTTP method */
  method?: "get" | "post";

  /** Parameters to send with the request */
  params?: FetchParameter[];

  /** Service function to call - use this OR url */
  serviceFn?: () => Promise<any>;

  /** Function to transform API response into FieldOption[] */
  formatter: (data: any) => FieldOption<T>[];

  /** Whether to fetch on mount */
  fetchOnMount?: boolean;

  /** Dependencies - refetch when these field values change */
  dependencies?: string[];
}

/**
 * Configuration for select/dropdown fields
 */
export interface SelectFieldConfig<T = any> extends BaseFieldConfig<T> {
  type: FieldType.SELECT;

  /** Static options */
  options?: FieldOption<T>[];

  /** Configuration for fetching options dynamically */
  fetch?: FetchConfig<T>;

  /** Whether to allow clearing the selection */
  allowClear?: boolean;

  /** Whether to show search functionality */
  showSearch?: boolean;

  /** Placeholder for search input */
  searchPlaceholder?: string;

  /** Callback when options are loaded/set */
  onOptionsLoaded?: (options: FieldOption<T>[]) => void;
}

/**
 * Configuration for checkbox fields
 */
export interface CheckboxFieldConfig<T = any> extends BaseFieldConfig<T[]> {
  type: FieldType.CHECKBOX;

  /** Options for checkboxes */
  options: FieldOption<T>[];

  /** Layout direction */
  layout?: "horizontal" | "vertical";

  /** CSS class for individual checkbox items */
  itemClassName?: string;

  /** Whether to select all by default */
  selectAll?: boolean;
}

/**
 * Configuration for radio fields
 */
export interface RadioFieldConfig<T = any> extends BaseFieldConfig<T> {
  type: FieldType.RADIO;

  /** Options for radio buttons */
  options: FieldOption<T>[];

  /** Layout direction */
  layout?: "horizontal" | "vertical";

  /** CSS class for individual radio items */
  itemClassName?: string;

  /** Custom onClick handler - receives the clicked option value */
  onClick?: (value: T) => void;
}

/**
 * Date picker modes
 */
export enum DatePickerMode {
  DATE = "date",
  MONTH = "month",
  YEAR = "year",
  WEEK = "week",
}

/**
 * Configuration for date picker fields
 */
export interface DateFieldConfig extends BaseFieldConfig<Date> {
  type: FieldType.DATE;

  /** Date picker mode */
  mode?: DatePickerMode;

  /** Minimum selectable date */
  minDate?: Date;

  /** Maximum selectable date */
  maxDate?: Date;

  /** Date format for display */
  dateFormat?: string;

  /** Whether to show time selector */
  showTime?: boolean;

  /** Callback when date is selected */
  onDateSelect?: (date: Date) => void;

  /** Default value for the date picker - can be used to set initial view */
  defaultPickerValue?: dayjs.Dayjs;
}

/**
 * Union type of all field configurations
 */
export type FieldConfig =
  | TextFieldConfig
  | TextAreaFieldConfig
  | SelectFieldConfig
  | CheckboxFieldConfig
  | RadioFieldConfig
  | DateFieldConfig;

/**
 * Form schema - collection of field configurations
 */
export interface FormSchema {
  fields: FieldConfig[];
}

/**
 * Props for field components
 */
export interface FieldComponentProps<T extends FieldConfig = FieldConfig> {
  config: T;
  control: any; // Control from react-hook-form
  errors?: any; // Field errors from react-hook-form
  watch?: any; // Watch function from react-hook-form
}

/**
 * Field renderer function signature
 */
export type FieldRenderer = (props: FieldComponentProps) => JSX.Element | null;

/**
 * Field registry entry
 */
export interface FieldRegistryEntry {
  type: FieldType;
  component: FieldRenderer;
}
